import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Input, Button, VDBText, Spacer } from "@components/atoms";
import { ButtonType, ButtonCategory } from "@components/atoms/Button/types";
import { useTranslation } from "react-i18next";
import { schema } from "../schema/otpSchema";
import { Keyboard, TouchableOpacity } from "react-native";
import styled from "styled-components/native";
import { SpaceType } from "@components/atoms/Spacer/types";
import useScreenOptionsHooks from "../../../../hooks/useScreenOptionsHooks";
import {
  EnterOtpFormProps,
  MfaEventNames,
  MfaEventTypes,
  OtpFormProps,
} from "../Types/types";
import { useUserMfaSendOtp, useUserMfaValidateOtp } from "@apis/user";
import ContactUsModal from "@components/molecules/ContactUs";
import WarningMessage from "@components/atoms/WarningMessage";
import { WarningType } from "@components/atoms/WarningMessage/types";
import { useAutoEnterKeySubmit } from "../../../../hooks/useAutoEnterKeySubmit";
import { messageType } from "@components/molecules/PopUp/types";
import Popup from "@components/molecules/PopUp";
import { useUserAnalytics } from "../../../../hooks/useAnalytics";

const EnterOtpForm: React.FC<EnterOtpFormProps> = ({
  onSuccess,
  user_id,
  email,
  password,
}) => {
  const { t } = useTranslation();
  const RESEND_TIME = 60;
  const [code, setCode] = useState<string>("");
  const [resendCodeCount, setResendCodeCount] = useState(0);
  const [canResendCode, setCanResendCode] = useState(false);
  const [loading, setLoading] = useState({
    resendCodeButton: false,
    loginButton: false,
  });
  const timerRef = useRef<any | null>(null);
  const isResendInProgress = useRef(false);
  const [countdown, setCountdown] = useState<number>(RESEND_TIME);
  const [contactUsState, setContactUsState] = useState({
    showContactUsModal: false,
    showContactUsSuccessPopup: false,
    contactUsSuccessResponseData: {
      title: "",
      message: "",
    },
  });
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<OtpFormProps>({
    resolver: yupResolver(schema(t)),
  });
  const { theme, typography } = useScreenOptionsHooks();
  const mfaValidateOtp = useUserMfaValidateOtp();
  const mfaResendOtp = useUserMfaSendOtp();
  const { trackEvent } = useUserAnalytics();
  const setLoader = (resendCodeButton: boolean, loginButton: boolean) => {
    setLoading({
      resendCodeButton,
      loginButton,
    });
  };

  const setCoundownInterval = () => {
    setCountdown(RESEND_TIME);
    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setCanResendCode(true);
          isResendInProgress.current = false;
          return 0;
        }
        if (prev > 1 && canResendCode) {
          setCanResendCode(false);
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    setCoundownInterval();
    trackEvent(MfaEventNames.MFA, {
      action: MfaEventTypes.VIEW_ENTER_OTP_FORM,
      user_id,
    });
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const formatSeconds = (seconds: number) => {
    const m = Math.floor(seconds / 60)
      .toString()
      .padStart(2, "0");
    const s = (seconds % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const onSubmit = (data: OtpFormProps) => {
    !theme.isLargeScreen && Keyboard.dismiss();
    const _code = data.code.trim();
    validateOtp({ code: _code });
  };

  useAutoEnterKeySubmit(
    handleSubmit(onSubmit),
    code.length !== 6 || loading.loginButton
  );

  const handleResendCode = () => {
    if (isResendInProgress.current) return;
    isResendInProgress.current = true;
    setLoader(true, false);
    trackEvent(MfaEventNames.MFA, {
      action: MfaEventTypes.CLICK_ENTER_OTP_RESEND_CODE,
      user_id,
    });
    if (canResendCode && resendCodeCount < 5) {
      mfaResendOtp.mutate(
        {
          user_id: user_id,
          password: password,
        },
        {
          onSuccess() {
            setCoundownInterval();
            setLoader(false, false);
            setResendCodeCount((prev) => prev + 1);
          },
          onError() {
            setLoader(false, false);
            isResendInProgress.current = false;
          },
        }
      );
    }
  };

  const validateOtp = async (data: OtpFormProps) => {
    setLoader(false, true);
    mfaValidateOtp.mutate(
      {
        otp: data.code,
        user_id: user_id,
        password: password,
      },
      {
        async onSuccess() {
          setLoader(false, false);
          trackEvent(MfaEventNames.MFA, {
            action: MfaEventTypes.EVENT_ENTER_OTP_CODE_VERIFIED,
            user_id,
          });
          onSuccess(true);
        },
        onError(error) {
          if (error) {
            if (
              error.response?.status === 422 &&
              error.response.data?.error === "Invalid OTP."
            ) {
              setLoader(false, false);
              control.setError("code", {
                type: "manual",
                message: t(
                  "login.enterOtp.input.code.incorrectCode.text",
                  "Incorrect code. Please try again."
                ),
              });
              trackEvent(MfaEventNames.MFA, {
                action: MfaEventTypes.EVENT_ENTER_OTP_INCORRECT,
                user_id,
              });
              return;
            } else if (
              error.response?.status === 410 &&
              error.response.data?.error ===
                "OTP has expired. Please request a new OTP."
            ) {
              setLoader(false, false);
              control.setError("code", {
                type: "manual",
                message: t(
                  "login.enterOtp.input.code.expired.text",
                  "The code has expired."
                ),
              });
              trackEvent(MfaEventNames.MFA, {
                action: MfaEventTypes.EVENT_ENTER_OTP_EXPIRED,
                user_id,
              });
              return;
            }
          }
          setLoader(false, false);
        },
      }
    );
  };

  return (
    <ParentContainer>
      <TopTextContainer>
        <VDBText color={theme["core-06"]} style={typography["Body-R"]}>
          {t(
            "login.enterOtp.topTitle.text",
            "A verification code has been sent to your email id"
          )}{" "}
          <VDBText color={theme["core-05"]} style={typography["Body-M"]}>
            {email}
          </VDBText>
        </VDBText>
      </TopTextContainer>

      <Spacer size={SpaceType.ExtraLarge} />

      <FormContainer>
        <Controller
          control={control}
          render={({ field: { onChange, onBlur, value } }) => (
            <Input
              inputLabel={t(
                "login.enterOtp.input.code.placeholderText",
                "Enter a 6 digits code"
              )}
              autoCapitalize="none"
              placeholder={t(
                "login.enterOtp.input.code.placeholderText",
                "Enter a 6 digits code"
              )}
              hideIconContainer={false}
              onBlur={onBlur}
              onChangeText={(text) => {
                if (/^\d+$/.test(text) || text === "") {
                  onChange(text);
                  setCode(text);
                }
              }}
              value={value ?? ""}
              error={errors?.code?.message}
              inputType="number-pad"
            />
          )}
          name="code"
        />

        <Spacer size={SpaceType.Big} />

        <ResendCodeContainer>
          {resendCodeCount < 5 ? (
            <RowContainer>
              <VDBText color={theme["core-06"]} style={typography["Body-R"]}>
                {t(
                  "login.enterOtp.resendCodeLabel.text",
                  "Didn't receive the code?"
                )}
              </VDBText>
              <Spacer size={SpaceType.Basic} />
              {canResendCode ? (
                loading.resendCodeButton ? (
                  <VDBText
                    color={theme["link-static"]}
                    style={typography["Body-M"]}
                  >
                    {t("login.enterOtp.resendCode.text", "Resend")}
                  </VDBText>
                ) : (
                  <TouchableOpacity onPress={handleResendCode}>
                    <VDBText
                      color={theme["link-static"]}
                      style={typography["Body-M"]}
                    >
                      {t("login.enterOtp.resendCode.text", "Resend")}
                    </VDBText>
                  </TouchableOpacity>
                )
              ) : (
                <RowContainer>
                  <VDBText
                    color={theme["core-06"]}
                    style={typography["Body-R"]}
                  >
                    {t("login.enterOtp.resendIn.text", "Resend in")}{" "}
                  </VDBText>
                  <VDBText
                    color={theme["core-06"]}
                    style={typography["Body-R"]}
                  >
                    {formatSeconds(countdown)}
                  </VDBText>
                </RowContainer>
              )}
            </RowContainer>
          ) : (
            <WarningMessage
              type={WarningType.WARNING}
              message={t(
                "login.enterOtp.resendCodeReachMaxAttemps.text",
                "You've reached the maximum number of resend code attempts."
              )}
            />
          )}
        </ResendCodeContainer>

        <Spacer size={SpaceType.Big} />

        <Button
          loading={loading.loginButton}
          type={ButtonType.primary}
          disabled={code.length !== 6 || loading.loginButton}
          category={ButtonCategory.default}
          title={t("login.button.login.text")}
          onPress={handleSubmit(onSubmit)}
          textTypographyStyle="H3-M"
        />
      </FormContainer>

      <Spacer size={SpaceType.ExtraLarge} />

      <ContactUsContainer>
        <VDBText color={theme["core-06"]} style={typography["Body-R"]}>
          {t("login.enterOtp.needHelpLabel.text", "Need help?")}
        </VDBText>
        <Spacer size={SpaceType.ExtraSmall} />
        <TouchableOpacity
          onPress={() => {
            Keyboard.dismiss();
            setTimeout(() => {
              setContactUsState({
                showContactUsModal: true,
                showContactUsSuccessPopup: false,
                contactUsSuccessResponseData: {
                  title: "",
                  message: "",
                },
              });
            }, 50);
          }}
        >
          <VDBText color={theme["link-static"]} style={typography["Body-M"]}>
            {t("login.enterOtp.contactUs.text", "Contact Us")}
          </VDBText>
        </TouchableOpacity>
      </ContactUsContainer>
      <ContactUsModal
        isOpen={contactUsState.showContactUsModal}
        onClose={() => {
          setContactUsState((prev) => ({
            ...prev,
            showContactUsModal: false,
            showContactUsSuccessPopup: false,
          }));
          Keyboard.dismiss();
        }}
        onResponseDataChange={(data: any) => {
          setContactUsState((prev) => ({
            ...prev,
            contactUsSuccessResponseData: data,
          }));
        }}
        showSuccess={() => {
          setContactUsState((prev) => ({
            ...prev,
            showContactUsModal: false,
            showContactUsSuccessPopup: true,
          }));
          Keyboard.dismiss();
        }}
        screenName="Verify Your Account"
      />
      {contactUsState.showContactUsSuccessPopup && (
        <Popup
          type={messageType.Success}
          title={contactUsState.contactUsSuccessResponseData?.title}
          buttons={[
            {
              type: ButtonType.primary,
              title: t("contactUs.successPopUp.button.title.text"),
              onPressAction: () => {
                setContactUsState({
                  showContactUsModal: false,
                  showContactUsSuccessPopup: false,
                  contactUsSuccessResponseData: {
                    title: "",
                    message: "",
                  },
                });
                Keyboard.dismiss();
              },
              category: ButtonCategory.default,
            },
          ]}
          HtmlSource={{
            html: `${contactUsState.contactUsSuccessResponseData?.message}`,
          }}
          styleKey="Body-R"
        />
      )}
    </ParentContainer>
  );
};

const ParentContainer = styled.View`
  padding: ${({ theme }) =>
    theme.isLargeScreen
      ? `0 ${SpaceType.Double}px ${SpaceType.ExtraLarge}px`
      : `${SpaceType.Double}px ${SpaceType.Double}px ${SpaceType.ExtraLarge}px`};
  width: 100%;
  max-width: ${({ theme }) => (theme.isMobile ? "100%" : "600px")};
  margin: 0 auto;
`;

const TopTextContainer = styled.View``;

const RowContainer = styled.View`
  flex-direction: row;
`;

const ResendCodeContainer = styled.View``;

const FormContainer = styled.View`
  width: 100%;
`;

const ContactUsContainer = styled.View`
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

export default EnterOtpForm;
